import { WHATSAPP_COUNTRY_CODES } from "../constants/whatsappCountryCodes";

const getBodyPlaceholders = (text) => {
  if (!text) return [];
  const regex = /{{(\d+)}}/g;
  const found = new Set();
  let match;
  while ((match = regex.exec(text)) !== null) {
    found.add(match[1]);
  }
  return [...found].sort((a, b) => Number(a) - Number(b));
};

// Splits a WhatsApp phone button's concatenated "+<countryCode><number>" back into
// the countryCode/phoneNumber pair TemplateCreationBody's phone button form expects.
// Country codes aren't fixed-width, so this matches the longest known prefix.
const splitPhoneNumber = (fullNumber) => {
  if (!fullNumber) return { countryCode: "", phoneNumber: "" };
  const sortedCodes = [...WHATSAPP_COUNTRY_CODES].sort((a, b) => b.length - a.length);
  const match = sortedCodes.find((code) => fullNumber.startsWith(code));
  if (!match) return { countryCode: "", phoneNumber: fullNumber.replace(/^\+/, "") };
  return { countryCode: match, phoneNumber: fullNumber.slice(match.length) };
};

const CATEGORY_LABELS = { MARKETING: "Marketing", UTILITY: "Utility" };

/**
 * Reverse-maps a Graph API template (fetched with `example` fields intact via
 * fetchTemplateForEdit) into the flat form shape templateCreationSlice /
 * TemplateCreationBody expect, so the existing "Create Template" wizard (opened
 * at step 2) can be reused to edit it.
 *
 * Only handles what that wizard's forms can actually represent — Authentication
 * templates, carousels, and button combinations the create form doesn't support
 * (anything beyond quick reply / one phone number / URL buttons) are reported as
 * unsupported rather than silently mis-mapped or dropped. Media headers (IMAGE/
 * VIDEO/DOCUMENT) preselect the right type but leave the file empty, since a
 * previously-issued upload handle can't be safely resubmitted — the user re-uploads.
 */
export const buildEditFormState = (template) => {
  const category = (template.category || "").toUpperCase();
  if (!CATEGORY_LABELS[category]) {
    return { supported: false, reason: "Authentication templates can't be edited here." };
  }

  const components = template.components || [];
  if (components.some((c) => c.type?.toLowerCase() === "carousel")) {
    return { supported: false, reason: "Carousel templates can't be edited here yet." };
  }

  const buttonsComp = components.find((c) => c.type === "BUTTONS");
  const rawButtons = buttonsComp?.buttons || [];
  const supportedButtonTypes = new Set(["QUICK_REPLY", "PHONE_NUMBER", "URL"]);
  if (rawButtons.some((b) => !supportedButtonTypes.has((b.type || "").toUpperCase()))) {
    return { supported: false, reason: "This template has a button type that can't be edited here yet." };
  }
  const phoneButtonsRaw = rawButtons.filter((b) => b.type === "PHONE_NUMBER");
  if (phoneButtonsRaw.length > 1) {
    return { supported: false, reason: "This template can't be edited here yet." };
  }

  const headerComp = components.find((c) => c.type === "HEADER");
  const bodyComp = components.find((c) => c.type === "BODY");
  const footerComp = components.find((c) => c.type === "FOOTER");

  let header = "none";
  let headerData = "";
  if (headerComp) {
    const format = (headerComp.format || "").toUpperCase();
    if (format === "TEXT") {
      header = "text";
      headerData = headerComp.text || "";
    } else if (["IMAGE", "VIDEO", "DOCUMENT"].includes(format)) {
      header = format.toLowerCase();
      headerData = ""; // forces a fresh upload — see file header comment
    }
  }

  const bodyText = bodyComp?.text || "";
  const placeholders = getBodyPlaceholders(bodyText);
  const exampleValues = bodyComp?.example?.body_text?.[0] || [];
  const bodyVariables = placeholders.map((id, idx) => ({ id: Number(id), value: exampleValues[idx] || "" }));

  const quickReplies = rawButtons
    .filter((b) => b.type === "QUICK_REPLY")
    .map((b) => ({ label: b.text || "" }));

  const phoneButtons = phoneButtonsRaw.length
    ? (() => {
        const { countryCode, phoneNumber } = splitPhoneNumber(phoneButtonsRaw[0].phone_number);
        return [{ label: phoneButtonsRaw[0].text || "", countryCode, phoneNumber }];
      })()
    : [];

  const linkButtons = rawButtons
    .filter((b) => b.type === "URL")
    .map((b) => ({
      label: b.text || "",
      link: b.url || "https://",
      isLinkDynamic: Boolean(b.example?.length),
      dynamicVariableLabel: b.example?.[0] || "",
    }));

  return {
    supported: true,
    formState: {
      selectedCategory: CATEGORY_LABELS[category],
      templateId: template.name,
      language: template.language || "en",
      allowAutoCategoryChange: false,
      header,
      headerData,
      body: bodyText,
      bodyVariables,
      footer: footerComp?.text || "",
      buttons: { quickReplies, phoneButtons, linkButtons },
      buttonsMode:
        quickReplies.length || phoneButtons.length || linkButtons.length
          ? "buttons"
          : "none",
      editMode: true,
      editTemplateId: template.id,
    },
  };
};
